import React, { useState } from 'react';
import { motion } from 'motion/react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  Avatar,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  AccountBalance as AccountIcon,
  ChevronRight as ChevronRightIcon,
  Add as AddIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { pageVariants, sectionVariants, staggerContainer, staggerItem } from '../../animations/variants';

const AccountsListView = ({
  accounts,
  onAccountClick,
  searchQuery,
  onSearchChange,
  onRefresh,
  isRefreshing,
  isLoading,
  onAssignToAccounts,
  pagination,
  onPageChange,
  onPageSizeChange,
}) => {
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);

  // Use accounts directly - search is now handled server-side
  const displayedAccounts = accounts;

  // Handle select all checkbox
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allAccountIds = displayedAccounts.map(
        (account) => account.accountId || account.account_id
      );
      setSelectedAccountIds(allAccountIds);
    } else {
      setSelectedAccountIds([]);
    }
  };

  // Handle individual checkbox
  const handleSelectAccount = (accountId, event) => {
    event.stopPropagation();
    setSelectedAccountIds((prev) => {
      if (prev.includes(accountId)) {
        return prev.filter((id) => id !== accountId);
      } else {
        return [...prev, accountId];
      }
    });
  };

  // Get selected accounts
  const getSelectedAccounts = () => {
    return accounts.filter((account) => {
      const accountId = account.accountId || account.account_id;
      return selectedAccountIds.includes(accountId);
    });
  };

  // Handle assign button click
  const handleAssignClick = () => {
    if (onAssignToAccounts && selectedAccountIds.length > 0) {
      onAssignToAccounts(getSelectedAccounts());
    }
  };

  // Check if all filtered accounts are selected
  const isAllSelected = displayedAccounts.length > 0 &&
    displayedAccounts.every((account) => {
      const accountId = account.accountId || account.account_id;
      return selectedAccountIds.includes(accountId);
    });

  const isIndeterminate = selectedAccountIds.length > 0 && !isAllSelected;

  // Render permission sets cell with overflow handling
  const renderPermissionSets = (permissionSets) => {
    if (!permissionSets || permissionSets.length === 0) {
      return (
        <Typography
          sx={{
            fontFamily: 'Metropolis, sans-serif',
            color: '#999999',
            fontSize: '0.875rem',
          }}
        >
          -
        </Typography>
      );
    }

    const displaySets = permissionSets.slice(0, 2);
    const remaining = permissionSets.length - 2;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {displaySets.map((psName, idx) => (
          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 4,
                height: 4,
                borderRadius: '50%',
                backgroundColor: '#4698D3',
              }}
            />
            <Typography
              component="span"
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                color: '#4698D3',
                fontSize: '0.875rem',
                fontWeight: 500,
                textDecoration: 'underline',
                textDecorationColor: 'transparent',
                '&:hover': {
                  textDecorationColor: '#4698D3',
                },
              }}
            >
              {psName}
            </Typography>
          </Box>
        ))}
        {remaining > 0 && (
          <Typography
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              color: '#666666',
              fontSize: '0.75rem',
              fontWeight: 500,
              pl: 1.5,
            }}
          >
            +{remaining} more
          </Typography>
        )}
      </Box>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <Box sx={{ minHeight: '100vh', py: 4, px: 4, backgroundColor: '#F9FBFD' }}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '8px',
            border: '1px solid #E5E5E5',
            p: 3,
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                backgroundColor: '#4698D3',
                width: 48,
                height: 48,
              }}
            >
              <AssignmentIcon sx={{ fontSize: 24 }} />
            </Avatar>
            <Typography
              variant="h4"
              sx={{
                fontFamily: 'Bitter, Georgia, serif',
                fontWeight: 600,
                fontSize: '1.75rem',
                color: '#253E66',
              }}
            >
              Assignments
            </Typography>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '8px',
            border: '1px solid #E5E5E5',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            gap: 2,
            py: 6,
          }}
        >
          <CircularProgress size={40} sx={{ color: '#4698D3' }} />
          <Typography
            sx={{
              color: '#666666',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 500,
              fontSize: '0.95rem',
            }}
          >
            Loading accounts...
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Empty state - check pagination total for true empty state
  if (displayedAccounts.length === 0 && !searchQuery && (!pagination || pagination.total === 0)) {
    return (
      <Box sx={{ minHeight: '100vh', py: 4, px: 4, backgroundColor: '#F9FBFD' }}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '8px',
            border: '1px solid #E5E5E5',
            p: 3,
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                backgroundColor: '#4698D3',
                width: 48,
                height: 48,
              }}
            >
              <AssignmentIcon sx={{ fontSize: 24 }} />
            </Avatar>
            <Typography
              variant="h4"
              sx={{
                fontFamily: 'Bitter, Georgia, serif',
                fontWeight: 600,
                fontSize: '1.75rem',
                color: '#253E66',
              }}
            >
              Assignments
            </Typography>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '8px',
            border: '1px solid #E5E5E5',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            gap: 2,
            py: 6,
          }}
        >
          <Avatar
            sx={{
              width: 64,
              height: 64,
              backgroundColor: '#EBF5FB',
            }}
          >
            <AccountIcon sx={{ fontSize: 32, color: '#4698D3' }} />
          </Avatar>
          <Typography
            sx={{
              color: '#253E66',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              fontSize: '1.1rem',
              textAlign: 'center',
            }}
          >
            No AWS accounts found
          </Typography>
          <Typography
            sx={{
              color: '#666666',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 500,
              fontSize: '0.875rem',
              textAlign: 'center',
              maxWidth: 300,
            }}
          >
            Onboard AWS accounts to start managing permission set assignments
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      style={{
        minHeight: '100vh',
        paddingTop: 32,
        paddingBottom: 32,
        paddingLeft: 32,
        paddingRight: 32,
        backgroundColor: '#F9FBFD',
      }}
    >
      {/* Header */}
      <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          border: '1px solid #E5E5E5',
          p: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                backgroundColor: '#4698D3',
                width: 48,
                height: 48,
              }}
            >
              <AssignmentIcon sx={{ fontSize: 24 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: 'Bitter, Georgia, serif',
                  fontWeight: 600,
                  fontSize: '1.75rem',
                  color: '#253E66',
                }}
              >
                Assignments
              </Typography>
              {pagination && (
                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontSize: '0.875rem',
                    color: '#999999',
                    fontWeight: 500,
                  }}
                >
                  {pagination.total} account{pagination.total !== 1 ? 's' : ''} in the system
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {selectedAccountIds.length > 0 && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAssignClick}
                sx={{
                  backgroundColor: '#4698D3',
                  borderRadius: '6px',
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 2.5,
                  '&:hover': {
                    backgroundColor: '#3580B8',
                  },
                }}
              >
                Assign ({selectedAccountIds.length})
              </Button>
            )}
            <TextField
              placeholder="Search accounts..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              size="small"
              sx={{
                width: 280,
                '& .MuiOutlinedInput-root': {
                  fontFamily: 'Metropolis, sans-serif',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '6px',
                  '& fieldset': { borderColor: '#E5E5E5' },
                  '&:hover fieldset': { borderColor: '#4698D3' },
                  '&.Mui-focused fieldset': { borderColor: '#4698D3' },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#999999', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
            <IconButton
              onClick={onRefresh}
              disabled={isRefreshing}
              sx={{
                backgroundColor: '#F9FBFD',
                border: '1px solid #E5E5E5',
                borderRadius: '6px',
                '&:hover': {
                  backgroundColor: '#EBF5FB',
                  borderColor: '#4698D3',
                },
              }}
            >
              <RefreshIcon
                sx={{
                  color: '#4698D3',
                  animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              />
            </IconButton>
          </Box>
        </Box>
      </Paper>
      </motion.div>

      {/* Accounts Table */}
      <motion.div variants={sectionVariants}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          border: '1px solid #E5E5E5',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F9FBFD' }}>
                <TableCell padding="checkbox" sx={{ borderBottom: '1px solid #E5E5E5', width: 50 }}>
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={handleSelectAll}
                    sx={{
                      color: '#E5E5E5',
                      '&.Mui-checked': {
                        color: '#4698D3',
                      },
                      '&.MuiCheckbox-indeterminate': {
                        color: '#4698D3',
                      },
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    color: '#253E66',
                    borderBottom: '1px solid #E5E5E5',
                  }}
                >
                  AWS accounts
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    color: '#253E66',
                    borderBottom: '1px solid #E5E5E5',
                    width: 250,
                  }}
                >
                  Permission sets
                </TableCell>
                <TableCell sx={{ width: 40, borderBottom: '1px solid #E5E5E5' }} />
              </TableRow>
            </TableHead>
            <TableBody
              component={motion.tbody}
              variants={staggerContainer}
              initial="initial"
              animate="animate"
            >
              {displayedAccounts.map((account) => {
                const accountId = account.accountId || account.account_id;
                const accountName = account.accountName || account.name || accountId;
                const email = account.email || '';
                const permissionSets = account.permissionSets || [];

                return (
                  <TableRow
                    key={accountId}
                    component={motion.tr}
                    variants={staggerItem}
                    hover
                    onClick={() => onAccountClick(account)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#F9FBFD',
                      },
                      '&:last-child td': {
                        borderBottom: 'none',
                      },
                    }}
                  >
                    <TableCell padding="checkbox" sx={{ borderBottom: '1px solid #E5E5E5' }}>
                      <Checkbox
                        checked={selectedAccountIds.includes(accountId)}
                        onClick={(e) => handleSelectAccount(accountId, e)}
                        sx={{
                          color: '#E5E5E5',
                          '&.Mui-checked': {
                            color: '#4698D3',
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #E5E5E5' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            backgroundColor: '#EBF5FB',
                          }}
                        >
                          <AccountIcon sx={{ fontSize: 18, color: '#4698D3' }} />
                        </Avatar>
                        <Box>
                          <Typography
                            sx={{
                              fontFamily: 'Metropolis, sans-serif',
                              fontWeight: 600,
                              fontSize: '0.95rem',
                              color: '#4698D3',
                              textDecoration: 'underline',
                              textDecorationColor: 'transparent',
                              '&:hover': {
                                textDecorationColor: '#4698D3',
                              },
                            }}
                          >
                            {accountName}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: 'Metropolis, sans-serif',
                              fontWeight: 500,
                              fontSize: '0.8rem',
                              color: '#666666',
                            }}
                          >
                            {accountId}
                            {email && ` | ${email}`}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #E5E5E5' }}>
                      {renderPermissionSets(permissionSets)}
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #E5E5E5' }}>
                      <ChevronRightIcon sx={{ color: '#BDBDBD', fontSize: 20 }} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* No results for search */}
        {displayedAccounts.length === 0 && searchQuery && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 6,
            }}
          >
            <Typography
              sx={{
                color: '#666666',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 500,
                fontSize: '0.95rem',
              }}
            >
              No accounts match "{searchQuery}"
            </Typography>
          </Box>
        )}

        {pagination && (
          <TablePagination
            component="div"
            count={pagination.total}
            page={pagination.page}
            onPageChange={(e, newPage) => onPageChange(newPage)}
            rowsPerPage={pagination.pageSize}
            onRowsPerPageChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{
              borderTop: '1px solid #E5E5E5',
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontFamily: 'Metropolis, sans-serif',
              },
            }}
          />
        )}
      </Paper>
      </motion.div>
    </motion.div>
  );
};

export default AccountsListView;
